/** @jsx jsx */
import { ClassNames, css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronNextIcon } from '@coursera/cds-icons';
import { ToolbarTable } from '@coursera/coursera-ui';

import { ChangeLogTypes } from 'bundles/author-assignment-grading/constants/ChangeLog';
import {
  getLearnerCommentLabel,
  getPartnerCommentLabel,
} from 'bundles/author-assignment-grading/constants/CommentLabels';
import type { UserItemAction } from 'bundles/author-assignment-grading/models/ChangeLogRecord';
import ChangeLogRecord from 'bundles/author-assignment-grading/models/ChangeLogRecord';
import { generateChangeLogGroups } from 'bundles/author-assignment-grading/utils/changeLogGroupUtils';
import type { ChangeLogGroup } from 'bundles/author-assignment-grading/utils/changeLogGroupUtils';
import type Item from 'bundles/author-course/models/Item';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';

import _t from 'i18n!nls/author-assignment-grading';

const styles = {
  chevron: css`
    position: absolute;
    top: var(--cds-spacing-50);
    left: 0;
  `,
  expandButton: css`
    max-width: initial;
    text-align: left;

    &:hover {
      background-color: initial; /* remove almost invisible light blue background color on hover */
    }
  `,
  expandedRow: css`
    /* set borders for primary row of the group that has some rows expanded below
    this will create top part of border around all rows in the group */
    border: 1px solid var(--cds-color-grey-50);
    background-color: var(--cds-color-grey-25);

    &:hover {
      /* needs important otherwise coursera-ui uses the default */
      background-color: var(--cds-color-grey-25) !important;
    }

    /* Using :before and :after elements to style vertical and horizontal styling lines for expanded deduplicated rows. */

    /* Style expanded rows */
    & ~ tr[data-testid='expanded-row'] {
      background-color: white;
      border-left: 1px solid var(--cds-color-grey-50);
      border-right: 1px solid var(--cds-color-grey-50);

      &:hover {
        background-color: var(--cds-color-grey-25);
      }

      /* set to relative position so we can absolute position the styling lines */
      position: relative;

      /* style the horizontal and vertical styling lines for expanded rows */
      & > td:first-child > :first-child {
        /* move content of the first cell to the right so there is space for the lines */
        padding-left: var(--cds-spacing-300);

        /* Each vertical line is spanning through the whole row (besides the last expanded row and the first main row) */
        &::before {
          position: absolute;
          left: 18px;
          top: 0;
          content: '';
          display: block;
          height: 100%;
          width: 1px;
          border-left: 1px solid var(--cds-color-grey-50);
        }

        /* styling of the horizontal line */
        &::after {
          position: absolute;
          left: 18px;
          top: 24px;
          content: '';
          display: block;
          height: 1px;
          width: 12px;
          border-top: 1px solid var(--cds-color-grey-50);
        }
      }

      /* Vertical line of the last expanded row only spans from the top of the row to the horizontal line */
      & > td:first-child > :first-child.edgeInLastExpandedRow {
        &::before {
          height: 24px;
        }
      }

      /* Remove the styling lines from comment expanded rows. Querying the [colspan] attribute as it's used to render
      the row across all columns. */
      & > td:first-child[colspan] > :first-child {
        &::before {
          content: initial;
        }

        &::after {
          content: initial;
        }
      }
    }

    /* Make horizontal border between the last expanded row and first un-expanded row */
    & ~ tr[data-testid='expanded-row'] + tr:not([data-testid='expanded-row']) {
      border-top: 1px solid var(--cds-color-grey-50);
    }
  `,
  collapsedRow: css`
    border-top: 1px solid white;
    border-right: 1px solid white;
    border-left: 1px solid white;

    &:hover {
      /* needs important otherwise coursera-ui uses the default */
      background-color: var(--cds-color-grey-25) !important;
    }
  `,
  expandedMainRowStylingLine: css`
    /* Style the main row that has the chevron - move the vertical line 36px down so it won't intersect with the chevron */
    & > td:first-child::before {
      position: absolute;
      left: 18px;
      top: 36px;
      content: '';
      display: block;
      height: calc(100% - 35px); /* change size of the line by 35px (can't use 36px because the line is off by 0.5px) */
      width: 1px;
      border-left: 1px solid var(--cds-color-grey-50);
    }
  `,
  mainRowCommon: css`
    position: relative;

    & > td:first-child > :first-child {
      padding: 0 0 0 var(--cds-spacing-300);
    }
  `,
};

type Props = {
  grade?: ItemGrade;
  changeLogs: Array<UserItemAction>;
  profileNames: {
    [userId: string]: string;
  };
  currentItem: Item;
  sessionItem?: SessionItem;
};

type TableRow = {
  changeMessage: string;
  changeType: string;
  date: number;
  displayDate: object;
  key: string;
  expandContent?: JSX.Element | JSX.Element[][] | null;
  grade?: string | null;
};

const {
  itemOutcomeOverrideUpdate,
  itemOutcomeOverrideDelete,
  itemOutcomeHide,
  itemOutcomeUnhide,
  itemCheatingCaseUpdate,
  itemCheatingCaseStatusUpdate,
  itemCheatingCaseEvidenceUpdate,
  ItemDeadlineOverrideUpdate,
  ItemAvailabilityOverrideUpdate,
  itemTimeLimitUpdate,
  itemMaxSubmissionPerTimedAttemptUpdate,
  itemMaxAttemptsUpdate,
  itemGradingComplete,
  itemProgressComplete,
} = ChangeLogTypes;

// groups the event types for the table filter tool. e.g. grouping all cheating case updates under one filter,
// or grouping all proctor config (item-setting exception) changes under one filter
const TYPE_FILTERS = {
  [itemOutcomeOverrideUpdate]: [itemOutcomeOverrideUpdate, itemOutcomeOverrideDelete],
  [itemOutcomeHide]: [itemOutcomeHide, itemOutcomeUnhide],
  [itemCheatingCaseUpdate]: [itemCheatingCaseUpdate, itemCheatingCaseStatusUpdate, itemCheatingCaseEvidenceUpdate],
  [itemGradingComplete]: [itemGradingComplete, itemProgressComplete],
  [itemProgressComplete]: [itemProgressComplete, itemGradingComplete],
  [ItemDeadlineOverrideUpdate]: [
    ItemDeadlineOverrideUpdate,
    ItemAvailabilityOverrideUpdate,
    itemTimeLimitUpdate,
    itemMaxSubmissionPerTimedAttemptUpdate,
    itemMaxAttemptsUpdate,
  ],
};

// filter options for the change log table
const getFilterOptions = ({ isAutoGraded }: $TSFixMe) => [
  { value: ChangeLogTypes.itemProgressStarted, label: _t('Started') },
  { value: ChangeLogTypes.ItemProgressSubmitted, label: _t('Submitted') },
  { value: ChangeLogTypes.itemGradingComplete, label: _t('Grade Saved') },
  { value: ChangeLogTypes.itemProgressComplete, label: isAutoGraded ? _t('Graded') : _t('Released') },
  { value: ChangeLogTypes.itemOutcomeOverrideUpdate, label: _t('Grade Override') },
  { value: ChangeLogTypes.itemOutcomeHide, label: _t('Grade Visibility') },
  { value: ChangeLogTypes.itemCheatingCaseUpdate, label: _t('Plagiarism Update') },
  { value: ChangeLogTypes.ItemDeadlineOverrideUpdate, label: _t('Individual Exception') },
  { value: ChangeLogTypes.itemSubmissionUnsubmitted, label: _t('Unsubmitted Attempt') },
  { value: ChangeLogTypes.itemProgressReset, label: _t('Progress Reset') },
  { value: ChangeLogTypes.itemGradeReset, label: _t('Grade Reset') },
];

// returns changelog rows in the format that TableV2 understands
function generateTableRows({ changeLogs, profileNames, currentItem, sessionItem }: Props): Array<TableRow> {
  if (changeLogs.length === 0) {
    // TODO: render zero-state
    return [];
  }

  if (sessionItem == null) {
    // If sessionItem is null or undefined the below logic will break since `ChangeLogRecord` tries to use it.
    // As such, this check lets us remove some Typescript opt-outs below that would only apply to broken states anyway.
    return [];
  }

  const changeLogRecords = changeLogs.map((log) => new ChangeLogRecord(log, profileNames, currentItem, sessionItem));

  // To deduplicate change log records we are using `ChangeLogGroup` container that has a list of duplicate events.
  // Each `ChangeLogGroup` is rendered as a single line in the change log table. If there is more than one event
  // in the `ChangeLogGroup`, we will render a list of deduplicated events as expandable content.
  // `ChangeLogGroup` with a single event is rendered the same as a single event without any deduplication.
  const changeLogGroups: ChangeLogGroup[] = generateChangeLogGroups(changeLogRecords);

  return changeLogGroups
    .map((group: ChangeLogGroup): TableRow | null => {
      const record = group.changeLogs[0];
      const date = record.date.timestamp;
      const displayDate = record.date;
      const changeRecord = {
        changeMessage: record.message,
        changeType: record.type,
        date,
        displayDate,
        key: `${record.type}-${date}`,
        expandContent:
          group.changeLogs.length > 1
            ? group.changeLogs.map((changeLog, changeLogIndex) => {
                return [
                  <Typography2
                    component="p"
                    variant="bodyPrimary"
                    className={changeLogIndex === group.changeLogs.length - 1 ? 'edgeInLastExpandedRow' : ''}
                  >
                    {changeLog.message}
                  </Typography2>,
                  <Typography2 component="p" variant="bodyPrimary">
                    {changeLog.grade ? `${changeLog.grade.displayGrade}%` : '--'}
                  </Typography2>,
                  <Typography2 component="p" variant="bodyPrimary">
                    <span>{changeLog.date.displayDate}</span>
                    &nbsp;
                    <span>{changeLog.date.displayTime}</span>
                  </Typography2>,
                ];
              })
            : null,
      };

      // handle type-specific data
      switch (record.type) {
        // override-updates have a `grade` and optional `explanation`
        case ChangeLogTypes.itemOutcomeOverrideUpdate:
          return {
            ...changeRecord,
            grade: record.grade.displayGrade,
            expandContent:
              record.explanation || record.partnerExplanation ? (
                <div className="expand-content">
                  {record.explanation && (
                    <div className="comment">
                      <Typography2 variant="subtitleMedium" component="label">
                        {getLearnerCommentLabel()}
                      </Typography2>
                      <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
                        {record.explanation}
                      </Typography2>
                    </div>
                  )}
                  {record.partnerExplanation && (
                    <div className="comment">
                      <Typography2 variant="subtitleMedium" component="label">
                        {getPartnerCommentLabel()}
                      </Typography2>
                      <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
                        {record.partnerExplanation}
                      </Typography2>
                    </div>
                  )}
                </div>
              ) : null,
          };

        // these types may have an optional `grade`
        case ChangeLogTypes.itemOutcomeOverrideDelete:
        case ChangeLogTypes.itemOutcomeUnhide:
        case ChangeLogTypes.itemOutcomeHide:
        case ChangeLogTypes.itemProgressComplete:
        case ChangeLogTypes.itemGradingComplete:
          return {
            ...changeRecord,
            grade: record.grade ? record.grade.displayGrade : null,
          };

        // plagiarism updates
        case ChangeLogTypes.itemCheatingCaseUpdate: // DEPRECATED: [FLEX-21803] support `itemCheatingCaseUpdate` event type until backfilled
        case ChangeLogTypes.itemCheatingCaseStatusUpdate:
        case ChangeLogTypes.itemCheatingCaseEvidenceUpdate:
          return {
            ...changeRecord,
            expandContent: record.partnerExplanation ? (
              <div className="expand-content">
                <Typography2 variant="subtitleMedium" component="label">
                  {getPartnerCommentLabel()}
                </Typography2>
                <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
                  {record.partnerExplanation}
                </Typography2>
              </div>
            ) : null,
          };

        // individual learner exception updates
        case ChangeLogTypes.ItemDeadlineOverrideUpdate:
        case ChangeLogTypes.ItemAvailabilityOverrideUpdate:
        case ChangeLogTypes.itemTimeLimitUpdate:
        case ChangeLogTypes.itemMaxSubmissionPerTimedAttemptUpdate:
        case ChangeLogTypes.itemMaxAttemptsUpdate:
          return changeRecord;

        // simple types
        case ChangeLogTypes.itemProgressStarted:
        case ChangeLogTypes.ItemProgressSubmitted:
        case ChangeLogTypes.itemProgressReset:
        case ChangeLogTypes.itemGradeReset:
        case ChangeLogTypes.itemSubmissionUnsubmitted:
          return changeRecord;

        default:
          return null;
      }
    })
    .filter((record: TableRow | null): record is TableRow => record !== null)
    .sort((r1, r2) => r2.date - r1.date); // reverse chron order
}

export default function ChangeLogTable({ grade, changeLogs, profileNames, currentItem, sessionItem }: Props) {
  const { isAutoGraded } = currentItem;

  // Store list of expanded rows so we can have different styling for the main row of the expanded and un-expanded group
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

  // Update expanded rows whenever user expands or collapse rows int the table
  const toggleExpandWrapper = (row: TableRow, rowIndex: number, toggleExpand: $TSFixMe) => {
    expandedRows[rowIndex] = !expandedRows[rowIndex];
    setExpandedRows(expandedRows);
    return toggleExpand(row);
  };

  return (
    <ClassNames>
      {({ css: cssClassName }) => {
        const expanded = cssClassName(styles.mainRowCommon, styles.expandedRow);
        const expandedWithStylingLine = cssClassName(
          styles.mainRowCommon,
          styles.expandedRow,
          styles.expandedMainRowStylingLine
        );
        const collapsed = cssClassName(styles.mainRowCommon, styles.collapsedRow);

        const selectMainRowStyle = (row: TableRow, rowIndex: number) => {
          if (expandedRows[rowIndex]) {
            // Duplicate events are passed as an array, which means it should have styling line included
            return Array.isArray(row.expandContent) ? expandedWithStylingLine : expanded;
          }
          return collapsed;
        };

        return (
          <ToolbarTable
            enableExpandableRows
            rowRootClassName={selectMainRowStyle}
            noResultCell={<Typography2 component="p">{_t('No result')}</Typography2>}
            toolbar={[
              {
                type: 'search',
                placeholder: _t('Search...'),
              },
              {
                type: 'dropdown',
                accessor: 'changeType',
                placeholder: (
                  <Typography2 component="span" color="supportText">
                    {_t('Filter by change type')}
                  </Typography2>
                ),
                options: getFilterOptions({ isAutoGraded }),
                matches: (value: $TSFixMe, changeType: $TSFixMe) => {
                  if (Array.isArray(TYPE_FILTERS[value])) {
                    return TYPE_FILTERS[value].includes(changeType);
                  }

                  return changeType === value;
                },
              },
            ]}
            columns={[
              {
                accessor: 'changeMessage',
                header: (
                  <Typography2
                    component="h4"
                    color="supportText"
                    variant="subtitleMedium"
                    css={{
                      display: 'inline',
                      paddingLeft: 'var(--cds-spacing-300)',
                    }}
                  >
                    {_t('Grade-related Change')}
                  </Typography2>
                ),
                cellRenderer: (
                  cell: $TSFixMe,
                  row: TableRow,
                  rowIndex: number,
                  secondaryIndex: number,
                  toggleExpand: $TSFixMe,
                  isExpanded: boolean
                ) =>
                  row.expandContent ? (
                    <Button
                      aria-label={isExpanded ? _t('Expand') : _t('Contract')}
                      size="small"
                      variant="ghost"
                      onClick={() => toggleExpandWrapper(row, rowIndex, toggleExpand)}
                      css={styles.expandButton}
                    >
                      {row.expandContent &&
                        (isExpanded ? (
                          <ChevronDownIcon size="small" css={styles.chevron} />
                        ) : (
                          <ChevronNextIcon size="small" css={styles.chevron} />
                        ))}
                      <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin change-message">
                        {row.changeMessage}
                      </Typography2>
                    </Button>
                  ) : (
                    <Typography2
                      component="p"
                      variant="bodyPrimary"
                      className="cui-p-margin change-message"
                      css={{
                        padding: '0',
                      }}
                    >
                      {row.changeMessage}
                    </Typography2>
                  ),
              },
              {
                accessor: 'grade',
                header: (
                  <Typography2 component="h4" color="supportText" variant="subtitleMedium">
                    {_t('Grade')}
                  </Typography2>
                ),
                width: 50,
                cellRenderer: (cell: $TSFixMe, row: $TSFixMe) => (
                  <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin change-grade">
                    {typeof row.grade === 'number' ? `${row.grade}%` : '--'}
                  </Typography2>
                ),
              },
              {
                accessor: 'date',
                header: (
                  <Typography2 component="h4" color="supportText" variant="subtitleMedium" css={{ display: 'inline' }}>
                    {_t('Change Made On')}
                  </Typography2>
                ),
                width: 160,
                cellRenderer: (cell: $TSFixMe, row: $TSFixMe) => (
                  <Typography2
                    component="p"
                    variant="bodyPrimary"
                    className="cui-p-margin change-date"
                    css={{
                      // keeping the first column to expand freely -> need to prevent wrapping of the time/date column
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span>{row.displayDate.displayDate}</span>
                    &nbsp;
                    <span>{row.displayDate.displayTime}</span>
                  </Typography2>
                ),
                sortable: true,
              },
            ]}
            rows={generateTableRows({ changeLogs, grade, profileNames, currentItem, sessionItem })}
          />
        );
      }}
    </ClassNames>
  );
}
